import React from 'react';
import { Box, Typography, Paper, Tooltip, Chip, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TimelineIcon from '@mui/icons-material/Timeline';
import SearchBox from '../../../FlowBuilder/Common/SearchBox/index.tsx';
import { BackButton } from '../../../Shared/BackButton/index.jsx';
import { useNavigate } from 'react-router';

type PaletteItem = {
  type: string;
  label: string;
  desc?: string;
  icon: React.ReactNode;
  color: string;
  tag?: string;
};

const chartItems: PaletteItem[] = [
  {
    type: 'bar',
    label: 'Bar Chart',
    desc: 'Compare categories',
    icon: <BarChartIcon />,
    color: '#1976d2',
    tag: 'Basic',
  },
  {
    type: 'stacked',
    label: 'Stacked Bar',
    desc: 'Parts of a whole',
    icon: <StackedBarChartIcon />,
    color: '#5e35b1',
    tag: 'Compare',
  },
  {
    type: 'line',
    label: 'Line Chart',
    desc: 'Trends over time',
    icon: <ShowChartIcon />,
    color: '#2e7d32',
    tag: 'Trend',
  },
  {
    type: 'area',
    label: 'Area Chart',
    desc: 'Trend with fill',
    icon: <TimelineIcon />,
    color: '#00897b',
    tag: 'Trend',
  },
  {
    type: 'pie',
    label: 'Pie Chart',
    desc: 'Share of total',
    icon: <PieChartIcon />,
    color: '#c62828',
    tag: 'Share',
  },
  {
    type: 'donut',
    label: 'Donut Chart',
    desc: 'Share with center',
    icon: <DonutLargeIcon />,
    color: '#ad1457',
    tag: 'Share',
  },
  {
    type: 'table',
    label: 'Data Table',
    desc: 'Raw, sortable data',
    icon: <TableChartIcon />,
    color: '#6d4c41',
    tag: 'Data',
  },
];

export default function ChartSidebar() {
  const [query, setQuery] = React.useState('');
  const navigate = useNavigate();

  const onDragStart = (e: React.DragEvent, chartType: string) => {
    e.dataTransfer.setData('chartType', chartType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const filtered = chartItems.filter(
    (i) =>
      i.label.toLowerCase().includes(query.toLowerCase()) ||
      i.type.toLowerCase().includes(query.toLowerCase()) ||
      (i.tag?.toLowerCase().includes(query.toLowerCase()) ?? false)
  );

  return (
    <Box
      sx={{
        width: 280,
        bgcolor: '#fff',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        gap: 0.75,
        height: '100%', // fill parent
        minHeight: 0, // allow child flex scrolling
      }}
    >
      {/* Fixed Header */}
      <Box sx={{ mb: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BackButton onClick={() => navigate(-1)} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          chart type
        </Typography>
      </Box>
      {/* Fixed Search (custom) */}
      <SearchBox
        value={query}
        placeholder="Search components…"
        handleSearch={(val: string) => setQuery(val)}
      />

      <Divider />

      {/* Scrollable area below */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 1, // keep scrollbar off content
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.2)', borderRadius: 8 },
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, pt: 1 }}>
          {filtered.map((item) => {
            const tint = alpha(item.color, 0.08);
            const ring = alpha(item.color, 0.24);
            return (
              <Tooltip key={item.type} title={item.desc ?? ''} placement="right">
                <Paper
                  draggable
                  onDragStart={(e) => onDragStart(e, item.type)}
                  elevation={0}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: tint,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'grab',
                    transition: 'all .18s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 18px ${alpha('#000', 0.08)}`,
                      borderColor: ring,
                    },
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  {/* Icon bubble */}
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '10px',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(item.color, 0.18),
                      color: item.color,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>

                  {/* Texts */}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        mb: 0.25,
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                      title={item.desc}
                    >
                      {item.desc}
                    </Typography>
                  </Box>

                  {/* Tag */}
                  {item.tag && (
                    <Chip
                      size="small"
                      label={item.tag}
                      sx={{
                        height: 22,
                        borderRadius: '10px',
                        bgcolor: alpha(item.color, 0.16),
                        color: item.color,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Paper>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
